import { decodePathSegment } from "@/lib/html-preview-document";

const URL_SCHEME = /^[a-z][a-z\d+.-]*:/i;
const LINE_SUFFIX = /:\d+(?::\d+)?$/;
const ROOTED_PATH = /^(?:\/|~(?:\/|$))/;
const HOME_ROOTED_PATH = /^(?:~|\/root)\/./;
const TRAILING_SLASHES = /\/+$/;
const NON_PATH_CHARACTER = /[\s()[\]{}<>=,;'"`$|*?!&]/;
const FILE_EXTENSION = /\.([a-z\d]*[a-z][a-z\d]*)$/i;
// js は `Node.js` のような製品名と見分けられないため含めない (`src/index.js` のようにフォルダ付きなら対象)。
const FILE_NAME_EXTENSIONS = new Set([
	...["png", "jpg", "jpeg", "gif", "webp", "svg", "mp4", "webm", "mov", "mkv", "avi"],
	...["pdf", "docx", "xlsx", "xlsm", "xls", "pptx", "csv", "tsv", "md", "txt", "log"],
	...["html", "htm", "json", "jsonl", "yaml", "yml", "toml", "ini", "cfg", "xml"],
	...["py", "ipynb", "ts", "tsx", "jsx", "sh", "css", "sql"],
	...["npy", "npz", "pkl", "pt", "pth", "ckpt", "onnx", "safetensors", "parquet", "h5", "hdf5"],
	...["mcap", "bag", "zip", "tar", "gz", "tgz"],
]);

/**
 * Markdown のリンク先がワークスペースのファイルを指すなら、そのパスを返す。相対パスは `baseDir`
 * (ワークスペース相対のディレクトリ。空ならワークスペース直下) を基準にする。
 * スキーム付きの URL・プロトコル相対 URL・ページ内アンカーは null。`#L12` や `:12` の行指定は外す。
 */
export function filePathFromMarkdownHref(href: string | undefined, baseDir = ""): string | null {
	if (!href || href.startsWith("#") || href.startsWith("//") || URL_SCHEME.test(href)) return null;
	const withoutFragment = href.split("#", 1)[0];
	const path = decodePathSegment(withoutFragment).replace(LINE_SUFFIX, "").trim();
	if (path === "") return null;
	return baseDir === "" || ROOTED_PATH.test(path) ? path : `${baseDir}/${path}`;
}

/**
 * インラインコードの中身がワークスペースのファイルを指すパスらしければ、`:12` の行指定を外して返す。
 * 対象は `~/` か `/root/` で始まるパス (フォルダも含む)、`/` を含み拡張子で終わる相対パス、よく使う
 * 拡張子のファイル名。相対パスに拡張子を求めるのは `and/or` のような語を、ファイル名だけのものを
 * 拡張子の一覧で絞るのは `np.array` のようなコードをリンクにしないため。
 */
export function filePathFromInlineCode(code: string): string | null {
	const text = code.trim();
	if (text === "" || NON_PATH_CHARACTER.test(text)) return null;
	const path = text.replace(LINE_SUFFIX, "");
	// 行指定を外しても残るコロンは URL (`git@github.com:org/repo.git`、`127.0.0.1:8000/a.json`) とみなす。
	if (path.includes(":")) return null;
	if (HOME_ROOTED_PATH.test(path)) return path.replace(TRAILING_SLASHES, "");
	if (path.startsWith("/") || path.startsWith("~")) return null;

	const extension = FILE_EXTENSION.exec(path)?.[1];
	if (!extension) return null;
	return path.includes("/") || FILE_NAME_EXTENSIONS.has(extension.toLowerCase()) ? path : null;
}
